// Image query keys with hierarchical structure
export const imageKeys = {
  // Base key for all image-related queries
  all: ['images'] as const,
  
  // List queries
  lists: () => [...imageKeys.all, 'list'] as const,
  list: (filters?: ImageFilters) => [...imageKeys.lists(), { filters }] as const,
  userImages: (userId: string, filters?: ImageFilters) => [
    ...imageKeys.lists(), 
    'user', 
    userId, 
    { filters }
  ] as const,
  
  // Detail queries
  details: () => [...imageKeys.all, 'detail'] as const,
  detail: (id: string) => [...imageKeys.details(), id] as const,
  
  // Status queries
  statuses: () => [...imageKeys.all, 'status'] as const,
  statusCounts: () => [...imageKeys.statuses(), 'counts'] as const,
  userStatusCounts: (userId: string) => [...imageKeys.statuses(), 'user', userId] as const,
  
  // Infinite queries
  infinite: (filters?: ImageFilters) => [...imageKeys.lists(), 'infinite', { filters }] as const,
  infiniteUser: (userId: string, filters?: ImageFilters) => [
    ...imageKeys.lists(), 
    'infinite', 
    'user', 
    userId, 
    { filters }
  ] as const,
  
  // Admin queries
  admin: () => [...imageKeys.all, 'admin'] as const,
  adminAll: (page?: number, status?: string) => [
    ...imageKeys.admin(), 
    'all', 
    { page, status }
  ] as const,
  adminCounts: () => [...imageKeys.admin(), 'counts'] as const,
  
  // Favourite queries
  favourites: (limit?: number, offset?: number) => [
    ...imageKeys.all, 
    'favourites', 
    { limit, offset }
  ] as const,
  
  // Archive queries (admin only)
  archives: (limit?: number, offset?: number) => [
    ...imageKeys.all, 
    'archives', 
    { limit, offset }
  ] as const,
} as const

// Image filters interface
export interface ImageFilters {
  status?: 'pending' | 'approved' | 'rejected' | 'processing'
  sortBy?: 'createdAt' | 'updatedAt' | 'name'
  sortOrder?: 'asc' | 'desc'
  search?: string
  dateFrom?: string
  dateTo?: string
  limit?: number
  page?: number
}

// Type for all possible image query keys
export type ImageQueryKey = typeof imageKeys[keyof typeof imageKeys]

// Utility functions for image keys
export const imageKeyUtils = {
  // Check if a key is for user images
  isUserImageKey: (key: readonly unknown[]): boolean => {
    return key.includes('user') && key.includes('images')
  },
  
  // Extract user ID from user image key
  getUserIdFromKey: (key: readonly unknown[]): string | null => {
    const userIndex = key.indexOf('user')
    if (userIndex !== -1 && userIndex + 1 < key.length) {
      const userId = key[userIndex + 1]
      return typeof userId === 'string' ? userId : null
    }
    return null
  },
  
  // Check if key matches a pattern
  matchesPattern: (key: readonly unknown[], pattern: readonly unknown[]): boolean => {
    if (pattern.length > key.length) return false
    
    for (let i = 0; i < pattern.length; i++) {
      if (pattern[i] !== key[i]) return false
    }
    
    return true
  },
}

export default imageKeys